import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../utils/api';
import { ArrowRight, CheckCircle2, MapPinned } from 'lucide-react';
import { HOME_HERO_SLIDES, galleryImageAt } from '../data/siteGallery';
import IndustrialHeroSlider from '../components/IndustrialHeroSlider';
import IndustryCard from '../components/IndustryCard';
import { INDUSTRIES_SERVED } from '../data/industriesData';
import { AUTHORIZED_PARTNERS } from '../data/partnersData';
import { BROCHURE_PDF } from '../data/siteContent';

const COVING_MORTAR_FEATURED = {
  _id: 'sv-bond-coving-mortar',
  name: 'SV Bond Coving Mortar',
  thickness: 'Hygienic Radius',
  shortDescription:
    'Mixture of SV Bond 9022, SV Bond 550, and SV Bond 940 for seamless epoxy coving at wall-floor junctions to reduce bacterial harboring.',
  keyFeatures: [
    'Seamless look',
    'Enhanced hygiene',
    'Chemical resistance',
    'Aesthetic finish',
  ],
};

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await productsAPI.getAll({ featured: 'true', limit: 6 });
      setFeaturedProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorScheme = (product) => {
    const name = product.name?.toLowerCase() || '';

    if (name.includes('slf 936') && !name.includes('cs')) return {
      label: 'Pharmaceutical',
      bg: 'from-purple-50 to-white',
      border: 'border-purple-200',
      tagBg: 'bg-purple-600',
      itemBg: 'bg-purple-100',
      itemText: 'text-purple-700',
      link: 'text-purple-700',
    };

    if (name.includes('945 as')) return {
      label: 'Anti-Static',
      bg: 'from-amber-50 to-white',
      border: 'border-amber-200',
      tagBg: 'bg-amber-600',
      itemBg: 'bg-amber-100',
      itemText: 'text-amber-700',
      link: 'text-amber-700',
    };

    if (name.includes('pucrete')) return {
      label: 'Food Grade',
      bg: 'from-green-50 to-white',
      border: 'border-green-200',
      tagBg: 'bg-green-600',
      itemBg: 'bg-green-100',
      itemText: 'text-green-700',
      link: 'text-green-700',
    };

    if (name.includes('946')) return {
      label: 'Water Safe',
      bg: 'from-blue-50 to-white',
      border: 'border-blue-200',
      tagBg: 'bg-blue-600',
      itemBg: 'bg-blue-100',
      itemText: 'text-blue-700',
      link: 'text-blue-700',
    };

    if (name.includes('936 cs')) return {
      label: 'Heavy Duty',
      bg: 'from-neutral-50 to-white',
      border: 'border-neutral-200',
      tagBg: 'bg-brand-900',
      itemBg: 'bg-brand-100',
      itemText: 'text-brand-900',
      link: 'text-brand-900',
    };

    return {
      label: 'Utility',
      bg: 'from-neutral-50 to-white',
      border: 'border-neutral-200',
      tagBg: 'bg-brand-900',
      itemBg: 'bg-brand-100',
      itemText: 'text-brand-900',
      link: 'text-brand-900',
    };
  };

  const homeFeaturedProducts = featuredProducts.some(
    (p) => p?.name?.toLowerCase().includes('coving mortar')
  )
    ? featuredProducts
    : [COVING_MORTAR_FEATURED, ...featuredProducts];

  return (
    <div className="home-page">
      {/* Hero â€” dark band for contrast; slider images use object-cover to fill frame */}
      <section className="relative w-full overflow-hidden bg-[#071525] text-white">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-950 via-[#0b2238] to-[#071525]" />
        <div className="pointer-events-none absolute inset-0 industrial-grid opacity-[0.14]" />
        <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent,transparent_48px,rgba(255,255,255,0.03)_48px,rgba(255,255,255,0.03)_49px)]" />

        <div className="relative z-10 mx-auto max-w-[1600px] px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-16">
          {/* 40% text / 60% image â€” two columns from md, no overlap */}
          <div className="flex flex-col items-stretch gap-10 md:flex-row md:flex-nowrap md:items-center md:gap-8 lg:gap-10 md:min-h-[min(58vh,600px)]">
            <div className="order-2 min-w-0 text-left text-white md:order-none md:mb-0 md:w-[40%] md:shrink-0">
              <div className="w-full px-0 py-1 sm:py-2 md:py-2 md:pr-1 lg:pr-2">
                <h1 className="fade-in-up delay-100 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.65)] md:text-5xl lg:text-[2.6rem] xl:text-[3rem]">
                  <span className="text-white">Advanced Solutions for </span>
                  <span className="text-brand-300">Industrial Excellence</span>
                </h1>
                <p className="fade-in-up delay-200 mt-5 text-base font-normal leading-relaxed text-slate-100 [text-shadow:0_1px_16px_rgba(0,0,0,0.55)] md:text-lg">
                  Manufacturer of SV Bond and authorised applicator for systems from ELANTAS Beck, Tremco CPG, Sika, Asian Paints, and others â€” as per project requirements.
                </p>

                <div className="fade-in-up delay-200 mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
                  <div className="flex items-start gap-3 rounded-xl bg-black/25 px-4 py-3 backdrop-blur-sm">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" strokeWidth={2} />
                    <div>
                      <p className="text-sm font-semibold text-white">Quality-assured systems</p>
                      <p className="text-xs text-slate-300">ISO 9001:2015 Â· documented applications</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-xl bg-black/25 px-4 py-3 backdrop-blur-sm">
                    <MapPinned className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" strokeWidth={2} />
                    <div>
                      <p className="text-sm font-semibold text-white">Pan-India &amp; abroad</p>
                      <p className="text-xs text-slate-300">Trained teams Â· site execution</p>
                    </div>
                  </div>
                </div>

                <div className="fade-in-up delay-300 mt-10 flex flex-col gap-4 pb-1 sm:flex-row sm:items-center">
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center rounded-small bg-brand-600 px-8 py-3.5 text-base font-bold text-white shadow-lg transition hover:bg-brand-500"
                  >
                    Request consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                  <Link
                    to="/products"
                    className="inline-flex items-center justify-center rounded-small border-2 border-white/50 bg-white px-8 py-3.5 text-base font-bold text-brand-900 shadow-md transition hover:bg-slate-100"
                  >
                    Explore products
                  </Link>
                </div>
              </div>
            </div>

            <div className="order-1 min-h-0 min-w-0 flex-1 md:order-none md:w-[60%] md:min-w-0">
              <div className="relative -mx-4 min-h-[280px] overflow-hidden sm:mx-0 md:mx-0 md:min-h-[min(58vh,580px)] md:rounded-2xl md:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.55)]">
                <IndustrialHeroSlider slides={HOME_HERO_SLIDES} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-white border-b border-neutral-200 relative overflow-hidden">
        <div className="absolute inset-0 industrial-grid opacity-[0.03] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="flex flex-nowrap items-stretch justify-between gap-0 divide-x divide-neutral-100 overflow-x-auto text-center [scrollbar-width:none]">
            <div className="min-w-[4.5rem] flex-1 shrink-0 px-1 py-2 sm:min-w-0 sm:px-3 sm:py-4">
              <div className="text-2xl font-heading font-bold text-brand-900 mb-1 sm:text-3xl lg:text-4xl">25+</div>
              <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-wide sm:text-sm">Years experience</div>
            </div>
            <div className="min-w-[4.5rem] flex-1 shrink-0 px-1 py-2 sm:min-w-0 sm:px-3 sm:py-4">
              <div className="text-2xl font-heading font-bold text-brand-900 mb-1 sm:text-3xl lg:text-4xl">500+</div>
              <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-wide sm:text-sm">Projects Completed</div>
            </div>
            <div className="min-w-[5.5rem] flex-[1.15] shrink-0 px-1 py-2 sm:min-w-0 sm:px-3 sm:py-4">
              <div className="text-xl font-heading font-bold text-brand-900 mb-1 sm:text-2xl lg:text-4xl">SV Bond</div>
              <div className="text-[9px] leading-tight text-neutral-500 font-medium uppercase tracking-wide sm:text-xs lg:text-sm">
                Imported bulk material repacked &amp; marketed by Sai Vikas Coatings pvt ltd
              </div>
            </div>
            <div className="min-w-[4.5rem] flex-1 shrink-0 px-1 py-2 sm:min-w-0 sm:px-3 sm:py-4">
              <div className="text-xl font-heading font-bold text-brand-900 mb-1 whitespace-nowrap sm:text-2xl lg:text-4xl">25 Years</div>
              <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-wide sm:text-sm">Years in Field</div>
            </div>
            <div className="min-w-[4.5rem] flex-1 shrink-0 px-1 py-2 sm:min-w-0 sm:px-3 sm:py-4">
              <div className="text-2xl font-heading font-bold text-brand-900 mb-1 sm:text-3xl lg:text-4xl">90+</div>
              <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-wide sm:text-sm">Trained Technicians</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-4">
            Trusted by leading organisations
          </p>

          <p className="text-sm text-neutral-600 max-w-3xl mx-auto mb-4">
            <span className="font-semibold text-neutral-800">Partners &amp; authorisations:</span>{' '}
            {AUTHORIZED_PARTNERS.map((partner) => partner.name).join(', ')}.
          </p>

          <p className="text-sm text-neutral-600 max-w-2xl mx-auto">
            Zydus Cadila, Serum Institute of India, Sanofi India, and many more across pharma, food, and engineering{' '}
            <Link to="/clients" className="font-semibold text-brand-800 hover:underline">
              view all clients with names and references
            </Link>
            .
          </p>
        </div>
      </section>

      <section id="products" className="py-20 bg-neutral-50  overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="mb-12 lg:mb-0">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                The <span className="text-brand-900">SV Bond</span> Advantage
              </h2>
              <p className="text-lg text-neutral-600 mb-6">
                <strong className="font-semibold text-neutral-900">Sai Vikas Coatings Pvt. Ltd.</strong> supplies and applies industrial flooring and protective coatings. Imported epoxy is repacked and marketed under the SV Bond brand, alongside authorised systems from partners such as ELANTAS Beck, Tremco CPG, Sika, and Asian Paints.
              </p>

              <div className="space-y-6 mt-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-small bg-brand-100 text-brand-900">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="w-6 h-6"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"></path><path d="M6.453 15h11.094"></path><path d="M8.5 2h7"></path></svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-heading font-bold text-neutral-900">Authorised materials</h3>
                    <p className="mt-2 text-neutral-600">Access to specified industrial floorings, protective coatings, and waterproofing compounds through authorised distributor relationships.</p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-small bg-brand-100 text-brand-900">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="w-6 h-6"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m9 12 2 2 4-4"></path></svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-heading font-bold text-neutral-900">Trained application teams</h3>
                    <p className="mt-2 text-neutral-600">Technicians trained with principal suppliers for correct preparation and application on site.</p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-small bg-brand-100 text-brand-900">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="w-6 h-6"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-heading font-bold text-neutral-900">Pan-India and abroad</h3>
                    <p className="mt-2 text-neutral-600">Projects delivered for pharmaceuticals, food, automobiles, and engineering sectors across India and abroad.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-brand-200 transform translate-x-4 translate-y-4 rounded-large"></div>
              <img className="relative rounded-large shadow-custom w-full object-cover h-[500px]" src={galleryImageAt(5)} alt="SV Bond cleanroom flooring" />
              <div className="absolute bottom-8 left-8 bg-white p-6 rounded-small shadow-custom max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm font-bold text-neutral-900">ISO 9001:2015 Certified</span>
                </div>
                <p className="text-xs text-neutral-500">Quality-assured systems and ISO 9001:2015 certified operations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="services" className="py-20 bg-white  overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-900 font-semibold tracking-wide uppercase text-sm">Premium Formulations</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-heading font-bold text-neutral-900">Featured SV Bond Products</h2>
            <p className="mt-4 text-lg text-neutral-600">Our flagship epoxy systems engineered for diverse industrial applications</p>
          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="bg-neutral-50 rounded-large h-80 animate-pulse border-2 border-neutral-100"></div>
              ))
            ) : (
              homeFeaturedProducts.map((product) => {
                const colors = getColorScheme(product);
                return (
                  <div key={product._id} className={`bg-gradient-to-br ${colors.bg} rounded-large border-2 ${colors.border} p-8 hover:shadow-custom-hover transition-all group`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 ${colors.tagBg} text-white text-xs font-bold rounded-full uppercase`}>{colors.label}</span>
                      <span className="text-sm font-bold text-neutral-500">{product.thickness || 'Premium'}</span>
                    </div>
                    <h4 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{product.name}</h4>
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                      {product.shortDescription || 'High-performance industrial coating solution.'}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(product.keyFeatures && product.keyFeatures.length > 0 ? product.keyFeatures : ['Premium Quality', 'Durable']).map((feature, idx) => (
                        <span key={idx} className={`px-2 py-1 ${colors.itemBg} ${colors.itemText} text-xs rounded`}>{feature}</span>
                      ))}
                    </div>
                    <Link to="/products" className={`inline-flex items-center ${colors.link} font-bold text-sm hover:opacity-80 group-hover:gap-2 transition-all`}>
                      View product categories <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                );
              })
            )}
          </div>


          <div className="bg-gradient-to-r from-brand-900 to-brand-800 rounded-large p-10 text-center text-white">
            <h3 className="font-heading text-2xl font-bold mb-3">Explore Our Complete Product Range</h3>
            <p className="text-brand-100 mb-6 max-w-2xl mx-auto">We offer 18+ specialized SV Bond formulations for every industrial need. Download our comprehensive catalog for detailed specifications.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BROCHURE_PDF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-sm font-bold rounded-small text-white bg-transparent hover:bg-white hover:text-brand-900 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="download" aria-hidden="true" className="lucide lucide-download mr-2 w-4 h-4"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg>
                Download Full Catalog (PDF)
              </a>
              <a href="#contact" className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-sm font-bold rounded-small text-brand-900 bg-white hover:bg-brand-50 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="phone" aria-hidden="true" className="lucide lucide-phone mr-2 w-4 h-4"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg>
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-neutral-50 relative overflow-hidden  overflow-auto">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 to-transparent opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-900 font-semibold tracking-wide uppercase text-sm">Trusted Across Sectors</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-heading font-bold text-neutral-900">Industries We Serve</h2>
            <p className="mt-4 text-lg text-neutral-600">
              Pharmaceuticals, nutraceuticals, R&amp;D, laboratories, biotech, hospitals, food &amp; beverage,
              automotive, engineering, marine, and warehousing — specialised SV Bond systems for each environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES_SERVED.map((industry) => (
              <IndustryCard key={industry.id} industry={industry} />
            ))}
          </div>
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white p-8 rounded-large shadow-custom border border-neutral-200">
              <div className="text-left sm:text-center">
                <h3 className="font-heading text-xl font-bold text-neutral-900 mb-2">Don't see your industry?</h3>
                <p className="text-neutral-600 text-sm">We provide custom SV Bond solutions for specialized applications.</p>
              </div>
              <a href="#contact" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-small text-white bg-brand-900 hover:bg-brand-800 shadow-custom transition-all whitespace-nowrap">
                Discuss Your Project
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="arrow-right" aria-hidden="true" className="lucide lucide-arrow-right ml-2 w-4 h-4"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </a>
            </div>
          </div>
        </div>
      </section>
      <section id="contact" className="py-20 bg-brand-950 relative overflow-hidden ">
        <div className="absolute inset-0 industrial-grid opacity-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white rounded-large shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-10 lg:p-16 bg-brand-900 text-white flex flex-col justify-center">
                <h2 className="text-3xl font-heading font-bold mb-6">Ready to upgrade your facility?</h2>
                <p className="text-brand-100 mb-8 text-lg">Get a free site inspection and technical consultation. Our experts will recommend the perfect SV Bond system for your needs.</p>

                <div className="space-y-6">
                <div className="flex items-start">
                    <i className="fas fa-map-marker-alt text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Head office</h4>
                      <p className="text-gray-600">Sr. No. 14/13 Sai Nivas, <br />Shahid Mangal Pandey Marg, <br />Adarsh Colony, Belthika Nagar, <br />  Thergaon, Pune, Maharashtra 411033</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-map-marker-alt text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Regional Office</h4>
                      <p className="text-white-600">Ganesh Kannaik H.No. 681, Housing Board Camurlim - Goa, Pin - 403507</p>
                      <p className="text-white-300">Contact No. - +919270766117</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-tools text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Our Workshop</h4>
                      <p className="text-white-600">PMK Parking, Barne Estate, Datta Nagar, Thergaon, Pune - 411033</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-phone text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Phone</h4>
                      <p className="text-white-300">+91 9822 14 86 60,
                        +91 9921 51 68 80,
                        +91 9970 66 95 81</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fab fa-whatsapp text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">WhatsApp Us</h4>
                      <p className="text-white-600"><a href="https://wa.me/919970669581" target="_blank" rel="noopener noreferrer" className="hover:text-brand-300">+91 9970669581</a></p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-envelope text-[#0D2847] text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Email</h4>
                      <p className="text-white-600">info@saivikascoatings.com<br />saivikasent@yahoo.co.in</p>
                    </div>
                  </div>
                </div>


              </div>

              <div className="p-10 lg:p-16 bg-white">
                <form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">Full Name</label>
                    <input type="text" id="name" className="w-full px-4 py-3 rounded-small border border-neutral-300 focus:ring-2 focus:ring-brand-900 focus:border-brand-900 outline-none transition-all" placeholder="John Doe" />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-neutral-700 mb-1">Company Name</label>
                    <input type="text" id="company" className="w-full px-4 py-3 rounded-small border border-neutral-300 focus:ring-2 focus:ring-brand-900 focus:border-brand-900 outline-none transition-all" placeholder="Your Company Ltd." />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">Work Email</label>
                    <input type="email" id="email" className="w-full px-4 py-3 rounded-small border border-neutral-300 focus:ring-2 focus:ring-brand-900 focus:border-brand-900 outline-none transition-all" placeholder="john@company.com" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">Project Details</label>
                    <textarea id="message" rows="4" className="w-full px-4 py-3 rounded-small border border-neutral-300 focus:ring-2 focus:ring-brand-900 focus:border-brand-900 outline-none transition-all" placeholder="Tell us about your flooring requirements (area size, usage type, etc.)"></textarea>
                  </div>
                  <button type="submit" className="w-full bg-brand-900 text-white font-bold py-3.5 px-6 rounded-small hover:bg-brand-800 shadow-custom hover:shadow-custom-hover transition-all">
                    Request Free Quote
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
